import * as amplitude from '@amplitude/analytics-browser';
import { getStatisticsCookie, getUserCookie } from './cookie';
import { isThirdPartyAnalyticsAllowed } from './analytics-permission';
import { isInKuoriShell } from './native-shell';
import env from './env';

const trackingOptions = {
  ipAddress: false,
  language: false,
  // platform: false,
};

const { AMPLITUDE_API_KEY = '' } = env;

// Upper bound on how long a navigation will wait for an analytics send. Short
// enough that a blocked request stays invisible to the user, long enough that a
// healthy request on a slow connection still gets out.
const ANALYTICS_SETTLE_TIMEOUT_MS = 800;

// Upper bound on how long the consent flow will wait for Amplitude to
// initialise. With `fetchRemoteConfig: false` init no longer makes a network
// request of its own, so this should never be reached — it stays as the
// backstop that guarantees a user who accepts the cookie banner is never left
// staring at it, whatever a future SDK version decides to do during init.
const ANALYTICS_INIT_TIMEOUT_MS = 2000;
// All members are static — `Analytics` is a singleton namespace, never
// instantiated. We reference the class by name rather than `this` so the
// methods keep working when passed as detached references (e.g. as a callback
// or analytics-helper `func`), where the call-site receiver would otherwise be
// `undefined` and the `Analytics.safe(...)` wrapper would throw. Cf. toosa#529.
export default class Analytics {
  private static isInitialized = false;
  private static initializationPromise: Promise<void> | null = null;
  private static usesPersistentIdentityStorage = false;
  private static lastUserPropertiesHash: string | null = null;

  private static hashObject(obj: Record<string, any>): string {
    return JSON.stringify(obj);
  }

  // DEV-712. Consent alone is not enough in the native app: a pupil can accept
  // cookies, and Google's rule covers children AND users of unknown age. Read
  // live rather than cached, because the answer CHANGES mid-session — peikko is
  // mostly pre-login, so the product is unknown (deny) until activation, and an
  // adult only becomes known afterwards.
  //
  // isInKuoriShell() is false during SSR, which allows. That is correct here:
  // amplitude-browser only ever runs client-side, so there is no server render
  // in which an event could escape.
  private static thirdPartyAllowed(): boolean {
    return isThirdPartyAnalyticsAllowed({
      product: getUserCookie()?.product,
      isNativeShell: isInKuoriShell(),
    });
  }

  static async setTrackingOptions(user = {}): Promise<void> {
    // Both gates, not either: consent is the user's choice, the permission
    // check is the one we owe them regardless of what they tapped.
    const hasOptedIn = getStatisticsCookie() && Analytics.thirdPartyAllowed();
    const previousOperation = Analytics.initializationPromise;
    const operation = (
      previousOperation
        ? previousOperation.catch(() => undefined)
        : Promise.resolve()
    ).then(() => Analytics.applyTrackingOptions(hasOptedIn, user));

    // Claim the queue synchronously. A later caller can now only run after this
    // complete consent operation, including persistent init and identify.
    Analytics.initializationPromise = operation;

    try {
      await operation;
    } finally {
      // Do not clear a newer caller's queue tail.
      if (Analytics.initializationPromise === operation) {
        Analytics.initializationPromise = null;
      }
    }
  }

  // Bounded, because `consent.tsx` and `settings.tsx` await this before
  // `router.push`. `init()` makes a network request, so an unbounded await hands
  // an ad blocker — or any proxy that swallows rather than refuses a request —
  // the power to stop people entering the app entirely. Measured against the
  // real SDK: a request the blocker *rejects* settles in ~260ms, but a request
  // that never settles left `setTrackingOptions` pending indefinitely, which on
  // the consent screen means the Accept button does nothing, forever.
  //
  // Timing out is cheap. Identity continuity does not depend on this await —
  // the ids are captured before init — and the SDK queues anything tracked while
  // init is still in flight. We simply do not record initialisation as complete,
  // so the next consent action retries. See DEV-657.
  private static async initWithin(
    options: Record<string, unknown>,
  ): Promise<boolean> {
    let completed = false;
    const initialization = amplitude
      .init(AMPLITUDE_API_KEY, null, {
        // Off everywhere, and it has to be off BEFORE consent in particular.
        // Left on, the SDK fetches https://sr-client-cfg.amplitude.com/config/
        // during init and caches the answer under `AMP_remote_config_<key>` in
        // localStorage. Both happen with `optOut: true` and
        // `identityStorage: 'none'` still set, so a visitor who has not yet seen
        // the cookie banner — or who has already refused — still contacts a
        // third-party analytics host and still gets storage written on their
        // device. Verified in the browser and reproduced against the real SDK.
        //
        // We read nothing from that config: it carries session-replay and
        // diagnostics sampling settings for features we do not use. Turning it
        // off also makes init purely local, which is why the bound below almost
        // never has to do anything. See DEV-657.
        ...options,
        fetchRemoteConfig: false,
      })
      .promise.then(() => {
        completed = true;
      });

    await Analytics.settle(initialization, ANALYTICS_INIT_TIMEOUT_MS);

    if (!completed) {
      console.warn(
        `Analytics initialization did not complete within ${ANALYTICS_INIT_TIMEOUT_MS}ms (possibly blocked); continuing without it.`,
      );
    }

    return completed;
  }

  private static async applyTrackingOptions(
    hasOptedIn: boolean,
    user: Record<string, any>,
  ): Promise<void> {
    // Initialize only once
    if (!Analytics.isInitialized) {
      if (hasOptedIn) {
        try {
          if (
            !(await Analytics.initWithin({
              optOut: false,
              defaultTracking: false,
              trackingOptions,
            }))
          ) {
            return;
          }
          Analytics.isInitialized = true;
          Analytics.usesPersistentIdentityStorage = true;
          console.info(
            `User has opted-in into analytics tracking. (Thank you! This helps us make our product better, and we don't track any personal/identifiable data.`,
          );
        } catch (error) {
          // Ad blocker or Amplitude blocked - fail gracefully
          console.warn(
            'Analytics initialization failed (possibly ad blocker):',
            error,
          );
          return;
        }
      } else {
        try {
          if (
            !(await Analytics.initWithin({
              optOut: true,
              defaultTracking: false,
              identityStorage: 'none',
              trackingOptions,
            }))
          ) {
            return;
          }
          Analytics.isInitialized = true;
          console.info(`User has opted-out from analytics tracking.`);
        } catch (error) {
          // Ad blocker or Amplitude blocked - fail gracefully
          console.warn('Analytics opt-out initialization failed:', error);
          return;
        }
      }
    } else if (hasOptedIn && !Analytics.usesPersistentIdentityStorage) {
      try {
        const deviceId = amplitude.getDeviceId();
        const sessionId = amplitude.getSessionId();

        if (
          !(await Analytics.initWithin({
            optOut: false,
            defaultTracking: false,
            trackingOptions,
            deviceId,
            sessionId,
          }))
        ) {
          return;
        }
        Analytics.usesPersistentIdentityStorage = true;
      } catch (error) {
        console.warn(
          'Analytics persistent initialization failed (possibly ad blocker):',
          error,
        );
        return;
      }
    } else {
      Analytics.safe(() => amplitude.setOptOut(!hasOptedIn));
    }

    // Only identify if user properties have changed and user has opted in
    if (hasOptedIn && Analytics.isInitialized) {
      const userHash = Analytics.hashObject(user);
      if (userHash !== Analytics.lastUserPropertiesHash) {
        Analytics.setUserProperties(user);
        Analytics.lastUserPropertiesHash = userHash;
      }
    }
  }

  // Analytics must never break app functionality. When Amplitude is blocked
  // (ad blocker, network blocklist, browser extension, or an uninitialized
  // SDK), any of the calls below could throw — and callers fire them inline,
  // before navigation/login. Swallow so the UX flow always continues.
  private static safe(fn: () => void): void {
    try {
      fn();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('Analytics call failed (non-fatal):', error);
      }
    }
  }

  // Guards the paths that PRODUCE data. Deliberately not folded into `safe()`:
  // that also wraps `amplitude.setOptOut(!hasOptedIn)`, and suppressing it when
  // permission is denied would leave an SDK that was already initialised for an
  // adult opted IN once a pupil appears on the same device — the exact opposite
  // of what this gate is for.
  //
  // Blocking init alone is not sufficient either. amplitude-browser QUEUES
  // anything tracked before init() and flushes it once init happens, so a
  // pupil's events would sit in memory and be sent retroactively the moment an
  // adult consented. Dropping them here means they are never recorded at all.
  private static safeIfAllowed(fn: () => void): void {
    if (!Analytics.thirdPartyAllowed()) return;
    Analytics.safe(fn);
  }

  static logEvent(name, properties?) {
    Analytics.safeIfAllowed(() => amplitude.track(`${name}`, properties));
  }

  static logClick(buttonName, properties?) {
    Analytics.safeIfAllowed(() =>
      amplitude.track(`${buttonName}:click`, properties),
    );
  }

  static logSubmit(name, properties?) {
    Analytics.safeIfAllowed(() =>
      amplitude.track(`${name}:submit`, properties),
    );
  }

  // Both of these deliberately use the DEFAULT transport, not `beacon`.
  //
  // Amplitude's beacon transport reports success as soon as the browser accepts
  // the request for queuing: `navigator.sendBeacon` returns true for "queued",
  // never for "delivered", and the SDK turns that into `code: 200`, marks the
  // events ingested and drops them from its queue
  // (`analytics-browser/.../transports/send-beacon.js`). A request that then
  // fails on the wire produces no error, no retry and no recovery. Caught on
  // prod: the only failed request in a whole session was the cross-site,
  // no-cors beacon carrying `signin:complete`, and the SDK counted it as sent.
  //
  // What the default transport actually buys us, stated precisely: a request
  // aborted by the page going away is a REJECTED fetch, which goes through
  // retry handling and stays in this origin's localStorage queue. That is
  // exactly the case beacon reported as delivered and then dropped. It is not a
  // blanket guarantee — an explicit `flush()` runs with retries disabled
  // (`Destination.prototype.flush` defaults `useRetry` to false), so if
  // Amplitude answers with an HTTP error the events are fulfilled and discarded
  // rather than retried; only the scheduled flush passes `useRetry: true`.
  // Retries reuse the original `insert_id` and Amplitude dedupes on
  // `device_id` + `insert_id` for 7 days, so a resend after a longer gap can
  // duplicate.
  //
  // ⚠️ That queue is per ORIGIN, and "the user comes back" is expensive here.
  // peikko is a single host, `login.somebuddy.com`, while toosa is a different
  // registrable domain on FI (`palvelu.someturva.fi`), so the redirect does not
  // touch this queue at all. A left-behind event waits for the user's next
  // visit to peikko — their next LOGIN, not their next app open, which with
  // long-lived sessions can be weeks. It then lands with its original `time`,
  // so recent windows read low and quietly backfill later. That is why the
  // `/login` handoff awaits `settle()` instead of trusting the queue.
  //
  // `setTransport` was also the wrong tool for the job: it is global and
  // sticky, so one `:complete` event switched EVERY later event on the page to
  // the same fire-and-forget path, including submits and clicks. See DEV-656.
  // Both return the flush promise, so a caller that is about to navigate can
  // wait for it. Callers that do not care keep ignoring the return value.
  static logComplete(name, properties?): Promise<void> {
    return Analytics.trackAndFlush(`${name}:complete`, properties);
  }

  static logInitiate(name, properties?): Promise<void> {
    return Analytics.trackAndFlush(`${name}:initiate`, properties);
  }

  private static trackAndFlush(eventName: string, properties?): Promise<void> {
    // logComplete/logInitiate route here and NOT through safe(), so this needs
    // its own gate. `activation:complete` — the event this ticket was filed
    // over — is one of these.
    if (!Analytics.thirdPartyAllowed()) return Promise.resolve();

    try {
      amplitude.track(eventName, properties);
      return amplitude
        .flush()
        .promise.then(() => undefined)
        .catch(() => undefined);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('Analytics call failed (non-fatal):', error);
      }
      return Promise.resolve();
    }
  }

  // Wait for a pending send, but never longer than `timeoutMs`. Analytics must
  // not be able to hold up a redirect: a blocked or stalled request would
  // otherwise leave the user on a spinner.
  static settle(
    pending: Promise<void>,
    timeoutMs = ANALYTICS_SETTLE_TIMEOUT_MS,
  ): Promise<void> {
    return Promise.race([
      pending,
      new Promise<void>((resolve) => {
        setTimeout(resolve, timeoutMs);
      }),
    ]);
  }

  static setLocale(locale) {
    Analytics.safeIfAllowed(() => {
      const identify = new amplitude.Identify().set('locale', locale);
      amplitude.identify(identify);
    });
  }

  static setUserProperties(user) {
    Analytics.safeIfAllowed(() => {
      const identify = new amplitude.Identify();
      Object.keys(user).map((key) => identify.set(key, user[key]));
      amplitude.identify(identify);
    });
  }

  static getDeviceId(): string | undefined {
    try {
      return amplitude.getDeviceId();
    } catch {
      return undefined;
    }
  }

  static getSessionId(): string | undefined {
    try {
      return String(amplitude.getSessionId());
    } catch {
      return undefined;
    }
  }
}
